/**
 * T29/T30 (Thomas 14.07.): Doppel-Lohnart-Analyse — GETEILTE Logik für die
 * Excel-Import- UND die Zulagenblatt-Berechnung (beide 3g).
 *
 * Manche Zulagen werden bewusst auf MEHRERE Lohnarten gebucht — Baustellenzulage
 * auf 13 UND 14: die eine Lohnart ist die „primäre" (steht in der Quelle bzw. ist
 * im Matrix-Feld `lohnart` designiert), die andere ist REGEL-ERZEUGT (gleiche
 * Basis; die SV-frei/SV-pflichtig-Betrags-Splittung macht RZL). Ohne Erklärung
 * sieht das aus wie ein Duplikat.
 *
 * Diese Funktion liefert je Zeile: ist sie Teil einer Doppel-Buchung, ist sie die
 * regel-erzeugte (abgeleitete) Zeile, welche Lohnart-Partner es gibt — und ob das
 * Paar KAPUTT ist (unvollständig = erwarteter Partner fehlt / inkonsistent =
 * ungleiche Anzahl oder Basis). Kein mandant-spezifisches Hardcode (13/14 stehen
 * nirgends im Code) — die erwartete Paarung kommt aus der Mandant-Matrix.
 */
import type { CSSProperties } from "react";

/** art (Zulagenart bzw. excel_art) → erwartete Lohnarten (≥2) + designierte
 *  primäre Lohnart (Matrix-Feld `lohnart`). */
export type DoppelRegeln = Record<
  string,
  { expected: Array<number | string>; primary?: number | string }
>;

export type DoppelItem = {
  /** Gruppierungs-Schlüssel: Excel = "MA|art" (mehrere MA je Karte),
   *  Zulagenblatt = "art" (eine Karte = ein MA). */
  groupKey: string;
  /** Anzeigename + Schlüssel für den Matrix-Lookup. */
  art: string;
  lohnart: number | string | null | undefined;
  anzahl?: number | null;
  /** optionaler Basis-Betrag (Excel: Kosten) für den Inkonsistenz-Check;
   *  undefined = nicht prüfen (Zulagenblatt hat keine Kosten). */
  basis?: number | null;
};

export type DoppelResult = {
  isDoppel: boolean;
  isAbgeleitet: boolean;
  partner: Array<number | string>;
  problem: { kurz: string; warum: string } | null;
};

const norm = (x: number | string): string => String(x);
const sortLA = (a: number | string, b: number | string): number =>
  String(a).localeCompare(String(b), undefined, { numeric: true });

/**
 * @param items Zeilen (in Render-Reihenfolge — das Ergebnis-Array ist index-parallel).
 * @param regeln Mandant-Matrix (art → {expected, primary}).
 * @param opts.quellLohnarten optional: welche Lohnarten stehen QUELLE-seitig je
 *        groupKey (Excel: aus data.rows)? Wenn vorhanden, bestimmt das primary
 *        (alles nicht in der Quelle = abgeleitet). Sonst zählt regeln[art].primary
 *        (bzw. die kleinste erwartete/vorhandene Lohnart) als primär.
 */
export function analyzeDoppelLohnart(
  items: DoppelItem[],
  regeln: DoppelRegeln,
  opts: { quellLohnarten?: (groupKey: string) => Set<string> } = {},
): DoppelResult[] {
  const presentByGroup = new Map<string, Array<number | string>>();
  const itemsByGroup = new Map<string, DoppelItem[]>();
  for (const it of items) {
    const ia = itemsByGroup.get(it.groupKey) ?? [];
    ia.push(it);
    itemsByGroup.set(it.groupKey, ia);
    if (it.lohnart === null || it.lohnart === undefined) continue;
    const arr = presentByGroup.get(it.groupKey) ?? [];
    if (!arr.some((x) => norm(x) === norm(it.lohnart!))) arr.push(it.lohnart);
    presentByGroup.set(it.groupKey, arr);
  }
  const erwartetOf = (art: string): Array<number | string> | null => {
    const e = regeln[art]?.expected;
    return e && e.length >= 2 ? e : null;
  };

  return items.map((it): DoppelResult => {
    const none: DoppelResult = { isDoppel: false, isAbgeleitet: false, partner: [], problem: null };
    if (it.lohnart === null || it.lohnart === undefined) return none;
    const present = presentByGroup.get(it.groupKey) ?? [];
    const erwartet = erwartetOf(it.art);
    const isDoppel = present.length >= 2 || erwartet !== null;
    if (!isDoppel) return none;
    const partner = [...present].sort(sortLA);

    // primary/derived
    let isAbgeleitet = false;
    const quelle = opts.quellLohnarten?.(it.groupKey);
    if (quelle && quelle.size > 0) {
      isAbgeleitet = !quelle.has(norm(it.lohnart));
    } else {
      const primary = regeln[it.art]?.primary ?? [...(erwartet ?? present)].sort(sortLA)[0];
      isAbgeleitet = primary !== undefined && norm(it.lohnart) !== norm(primary);
    }

    // problem (gruppen-weit — erscheint auf jeder present-Zeile der Gruppe)
    let problem: DoppelResult["problem"] = null;
    if (erwartet) {
      const fehlt = erwartet.filter((e) => !present.some((p) => norm(p) === norm(e)));
      if (fehlt.length > 0) {
        problem = {
          kurz: `unvollständig: ${fehlt.join(", ")} fehlt`,
          warum:
            `Die Regel für „${it.art}“ verlangt die Lohnarten ${erwartet.join(" + ")}, `
            + `aber ${fehlt.join(", ")} fehlt in dieser Berechnung — ein fehlender Partner `
            + `bedeutet falschen Lohn. Import/Matrix prüfen.`,
        };
      }
    }
    if (!problem && present.length >= 2) {
      const grp = itemsByGroup.get(it.groupKey) ?? [];
      const anzahlUngleich = new Set(grp.map((x) => x.anzahl ?? null)).size > 1;
      const basisVals = grp.map((x) => x.basis).filter((v): v is number => typeof v === "number");
      const basisUngleich =
        basisVals.length === grp.length && new Set(basisVals.map((v) => Math.round(v * 100))).size > 1;
      if (anzahlUngleich || basisUngleich) {
        problem = {
          kurz: "inkonsistentes Paar",
          warum:
            `Die Lohnarten ${partner.join(" + ")} einer Doppel-Buchung müssen dieselbe Anzahl`
            + `${basisVals.length ? " und Basis" : ""} tragen (RZL splittet die Beträge erst danach) — `
            + `hier weichen sie ab. Prüfen.`,
        };
      }
    }
    return { isDoppel, isAbgeleitet, partner, problem };
  });
}

/** Tooltip für den „Regel"-Badge auf der abgeleiteten Zeile. */
export function doppelRegelTooltip(art: string, partner: Array<number | string>): string {
  return (
    `Regel-erzeugte Zeile: „${art}“ wird auf die Lohnarten ${partner.join(" + ")} gebucht. `
    + `Diese Lohnart steht NICHT in der Quelle — sie ist der regelbedingte Zwilling zur `
    + `Quell-Zeile (gleiche Basis; die SV-frei/SV-pflichtig-Betrags-Aufteilung macht RZL). `
    + `Keine Doppelzahlung.`
  );
}

export const DOPPEL_REGEL_BADGE: CSSProperties = {
  marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#1e40af",
  background: "#dbeafe", border: "1px solid #93c5fd", borderRadius: 3,
  padding: "0 4px", cursor: "help", whiteSpace: "nowrap",
};
export const DOPPEL_PROBLEM_BADGE: CSSProperties = {
  marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#991b1b",
  background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 3,
  padding: "0 4px", cursor: "help", whiteSpace: "nowrap",
};
